import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Lock } from "lucide-react";

import Select, { components } from "../../components/select";
import SortablePeopleList from "../../components/sortablePeople";
import Spinner from "../../components/spinner";
import { useLanguage } from "../../context/languageContext";
import { fetchAllSummaryOptions } from "../../helper";
import styles from "./SummarySections.module.scss";
// The automatic preview grid is the Picks editor's, so the two Most
// Recommended editors look the same.
import picksStyles from "../picksSections/PicksSections.module.scss";
import EditPageHeader, { EditPageLayout, SegmentedToggle, HeaderDeleteButton } from "../../components/editPageHeader";
import { useConfirm } from "../../components/confirmDialog";
import { titleMissing, TITLE_HINT } from "../../helper/sectionTitles";

const API = process.env.REACT_APP_API_ROOT;

const OPTION_STYLES = {
    container: { display: "flex", alignItems: "center", gap: "10px" },
    image: { width: "30px", height: "40px", objectFit: "cover", borderRadius: "4px" },
    name: { margin: 0 },
};

// Module scope so it is not a new component type each render, which would
// remount the menu mid-interaction.
const Option = React.memo((props) => {
    const { image, label } = props.data || {};
    return (
        <components.Option {...props}>
            <div style={OPTION_STYLES.container}>
                {image && <img src={image} alt={label} style={OPTION_STYLES.image} />}
                <p style={OPTION_STYLES.name}>{label}</p>
            </div>
        </components.Option>
    );
});

const SELECT_COMPONENTS = { Option };

/**
 * One section of the Summary tab, edited.
 *
 * The table says where a rail sits and how big it is; this says what is IN it.
 * "Automatic" was only ever a default — an admin who wants to choose the books
 * on Trending should be able to, which is what the source switch is for.
 *
 * For You and Journeys are the exception, and not as policy: they resolve per
 * reader from their own genres, so a hand-picked list would simply be ignored.
 * The page says that and offers the title only.
 */

const SOURCE_LABELS = {
    latest: "Automatic — the newest summaries",
    trending: "Automatic — what readers are opening",
    mostRecommended: "Automatic — books the most recommenders name",
    journeys: "Personalised",
    forYou: "Personalised",
    manual: "Hand-picked",
};

const EditSummarySection = () => {
    const navigate = useNavigate();
    const { sectionId } = useParams();
    const { adminInfo, selectedLang, languages, languageLabels } = useLanguage();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const confirm = useConfirm();
    const [section, setSection] = useState(null);
    const [titles, setTitles] = useState({});
    const [titleLang, setTitleLang] = useState("en");
    const [source, setSource] = useState("manual");
    const [items, setItems] = useState([]);
    const [options, setOptions] = useState([]);
    const [minRecommenders, setMinRecommenders] = useState(5);
    const [booksPerReader, setBooksPerReader] = useState(15);

    const headers = useMemo(
        () => ({ Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null }),
        [adminInfo]
    );

    useEffect(() => {
        if (!adminInfo?.token) return;
        let cancelled = false;

        (async () => {
            setLoading(true);
            try {
                const [{ data: response }, summaryOptions] = await Promise.all([
                    axios.get(`${API}/api/admin/summary/sections/get/${sectionId}`, { headers }),
                    fetchAllSummaryOptions(selectedLang, adminInfo.token),
                ]);
                if (cancelled) return;

                if (!response.status) {
                    toast.error(response.message || "Could not load the section.");
                    return;
                }
                const data = response.data || {};
                setSection(data);
                setSource(data.source || "manual");
                setItems(data.items || []);
                setMinRecommenders(data.minRecommenders ?? 5);
                setBooksPerReader(data.booksPerReader ?? 15);
                setTitles(
                    Object.fromEntries((data.translations || []).map((t) => [t.langCode, t.title]))
                );
                setOptions(
                    (summaryOptions || []).map((row) => ({
                        value: row.summaryId,
                        label: row.bookName || "Untitled summary",
                        image: row.image,
                    }))
                );
            } catch (error) {
                console.error("EditSummarySection load error::", error);
                toast.error("Could not load the section.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [adminInfo, sectionId, selectedLang, headers]);

    // The grid and the picker both speak react-select options, so members are
    // held in that shape and converted once on save.
    const itemOptions = useMemo(
        () =>
            items.map((item) => ({
                value: String(item.summaryId),
                label: item.bookName || "Untitled summary",
                image: item.image,
            })),
        [items]
    );

    const setItemsFromOptions = (selected) =>
        setItems(
            (selected || []).map((option) => ({
                summaryId: option.value,
                bookName: option.label,
                image: option.image,
            }))
        );

    const handleSave = async () => {
        const english = (titles.en || "").trim();
        if (!english) {
            toast.error("An English title is required.");
            return;
        }
        if (source === "manual" && items.length === 0) {
            // Saving an empty hand-picked rail would hide it from the app with
            // no sign of why — it would simply render nothing.
            toast.error("A hand-picked section needs at least one summary.");
            return;
        }

        const nativeMostRecommended = section.nativeSource === "mostRecommended";
        if (nativeMostRecommended && (!Number.isInteger(minRecommenders) || minRecommenders < 0)) {
            toast.error("Recommended by more than must be a whole number, 0 or more.");
            return;
        }

        const isForYou = section.source === "forYou";
        if (isForYou && (!Number.isInteger(booksPerReader) || booksPerReader < 1)) {
            toast.error("Books for each reader must be a whole number, 1 or more.");
            return;
        }

        setSaving(true);
        try {
            const values = {
                translations: (languages || ["en"])
                    .filter((code) => (titles[code] || "").trim())
                    .map((code) => ({ langCode: code, title: titles[code].trim() })),
            };
            if (isForYou) values.booksPerReader = booksPerReader;
            if (!section.personalised) {
                values.source = source;
                if (source === "manual") values.items = items.map((i) => String(i.summaryId));
                // Saved whichever way the rail is set, so switching back to
                // automatic later picks up the threshold left here.
                if (nativeMostRecommended) values.minRecommenders = minRecommenders;
            }

            const { data: response } = await axios.put(
                `${API}/api/admin/summary/sections/update/${sectionId}`,
                { values },
                { headers }
            );

            if (response.status) {
                toast.success(`"${english}" saved.`);
                navigate("/summary-sections");
            } else {
                toast.error(response.message || "Could not save the section.");
            }
        } catch (error) {
            console.error("EditSummarySection save error::", error);
            toast.error(error?.response?.data?.error || "Could not save the section.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        const title = (titles.en || "").trim() || section.key;
        const ok = await confirm({
            title: `Delete "${title}"?`,
            body: "It will be removed from the Summary tab. This can't be undone.",
            confirmLabel: "Delete",
        });
        if (!ok) return;

        setDeleting(true);
        try {
            const { data: response } = await axios.put(
                `${API}/api/admin/summary/sections/delete/${sectionId}`, {}, { headers }
            );
            if (response.status) {
                toast.success(`"${title}" removed.`);
                navigate("/summary-sections");
            } else {
                toast.error(response.message || "Could not remove the section.");
            }
        } catch (error) {
            console.error("EditSummarySection delete error::", error);
            toast.error(error?.response?.data?.error || "Could not remove the section.");
        } finally {
            setDeleting(false);
        }
    };

    if (loading) return <Spinner />;
    if (!section) return <div className="p-3 text-muted">That section no longer exists.</div>;

    const native = section.nativeSource || "manual";
    const locked = section.personalised === true;
    // Only a hand-picked rail an admin made can be removed — deleting a seeded
    // row would not remove the rail, only the ability to control it.
    const deletable = !locked && native === "manual";

    return (
        <EditPageLayout>
            <EditPageHeader
                title="Edit Summary Section"
                subtitle={titles.en || section.key}
                badge={locked && <Lock size={15} className="text-medium-emphasis" />}
                note={locked ? section.lockedReason : null}
                actions={
                    <>
                        {/* Where the members come from is a property of the
                            whole section, so it sits beside its name rather
                            than as one more field in the form. */}
                        {!locked && (
                            <SegmentedToggle
                                value={source === "manual" ? "manual" : "auto"}
                                onChange={(next) => setSource(next === "manual" ? "manual" : native)}
                                options={[
                                    {
                                        value: "auto",
                                        label: "Automatic",
                                        disabled: native === "manual",
                                        title: native === "manual"
                                            ? "This section was created hand-picked; it has no automatic list."
                                            : SOURCE_LABELS[native],
                                    },
                                    { value: "manual", label: "Hand-picked" },
                                ]}
                            />
                        )}
                        {deletable && (
                            <HeaderDeleteButton onClick={handleDelete} disabled={saving || deleting} />
                        )}
                        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate("/summary-sections")}>
                            Back
                        </button>
                    </>
                }
            />

            <div className={styles.editCard}>
                <label className="label">Title:</label>
                <div className="d-flex flex-wrap gap-1 mb-2">
                    {(languages || ["en"]).map((code) => (
                        <button
                            key={code}
                            type="button"
                            className={`btn btn-sm ${titleLang === code ? "btn-dark" : "btn-outline-secondary"}`}
                            onClick={() => setTitleLang(code)}
                        >
                            {languageLabels?.[code] || code}
                            {titleMissing(code, titles[code], titles.en) ? " •" : ""}
                        </button>
                    ))}
                </div>
                <input
                    className="form_control"
                    type="text"
                    placeholder={`Title in ${languageLabels?.[titleLang] || titleLang}...`}
                    value={titles[titleLang] || ""}
                    onChange={(e) => setTitles((prev) => ({ ...prev, [titleLang]: e.target.value }))}
                />
                <small className="text-muted d-block mt-1">{TITLE_HINT}</small>

                {locked ? (
                    <>
                        {section.source === "forYou" && (
                            <div className="mt-4">
                                <label className="label">Books for each reader:</label>
                                <input
                                    className="form_control"
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={Number.isNaN(booksPerReader) ? "" : booksPerReader}
                                    onChange={(e) =>
                                        setBooksPerReader(e.target.value === "" ? NaN : Number(e.target.value))}
                                />
                                <small className="text-muted">
                                    Dealt equally across the genres each reader chose — 30 over 3 genres
                                    is 10 each. The rail shows 12 and See All the whole set. Readers whose
                                    genres have fewer unread summaries get as many as there are.
                                </small>
                            </div>
                        )}
                        <p className="text-muted small mt-4 mb-0">
                            This rail is built for each reader from the genres they chose, so there is
                            no single list to pick. Its position and visibility are set from the
                            Summary Sections table.
                        </p>
                    </>
                ) : (
                    <div className="mt-4">
                        {source === "manual" && <label className="label d-block">Members:</label>}
                        {source !== "manual" && source === "mostRecommended" ? (
                            <MostRecommendedSettings
                                value={minRecommenders}
                                onChange={setMinRecommenders}
                                saved={section.minRecommenders ?? 5}
                                preview={section.mostRecommended}
                                dailyLimit={section.itemLimit > 0 ? Math.max(section.itemLimit, 12) : 20}
                            />
                        ) : source !== "manual" ? (
                            <p className="text-muted small mb-0">
                                {SOURCE_LABELS[source] || "Chosen by the app."} The app decides the
                                members and their order; you set the name and where the rail sits.
                                Switch to hand-picked to choose them yourself.
                            </p>
                        ) : (
                            <>
                                {/* The picker's own chips are hidden so the grid below is the
                                    single source of order and removal. Which is also why it
                                    does not clear and backspace is disarmed: with the chips
                                    hidden, those controls would wipe rows from a field that
                                    gives no sign it holds anything. */}
                                <Select
                                    className="form-control-md fw-medium p-0"
                                    isMulti
                                    markAdded
                                    isClearable={false}
                                    backspaceRemovesValue={false}
                                    controlShouldRenderValue={false}
                                    closeMenuOnSelect
                                    placeholder="Search summaries by title..."
                                    options={options}
                                    value={itemOptions}
                                    onChange={setItemsFromOptions}
                                    components={SELECT_COMPONENTS}
                                />

                                <div className="mt-2">
                                    <SortablePeopleList
                                        items={itemOptions}
                                        onChange={setItemsFromOptions}
                                        thumbShape="cover"
                                        variant="grid"
                                        emptyMessage="No summaries selected yet — search above."
                                    />
                                </div>
                                <small className="text-muted">
                                    This is the order they appear in the app. Drag to reorder, or type
                                    a position to move a row straight there.
                                </small>
                            </>
                        )}
                    </div>
                )}

                <div className="d-flex gap-2 justify-content-end mt-4">
                    <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => navigate("/summary-sections")}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-dark" disabled={saving} onClick={handleSave}>
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </EditPageLayout>
    );
};

/**
 * The automatic Most Recommended rail's controls — the same threshold and
 * "Today's books" preview the Picks editor has, over summaries instead of books
 * without one. The preview reflects the SAVED threshold; the server picks it.
 */
function MostRecommendedSettings({ value, onChange, saved, preview, dailyLimit }) {
    const books = preview?.preview || [];
    const changed = value !== saved;

    return (
        <>
            <div className="mb-3">
                <label className="label">Recommended by more than:</label>
                <input
                    className="form_control"
                    type="number"
                    min="0"
                    step="1"
                    value={Number.isNaN(value) ? "" : value}
                    onChange={(e) => onChange(e.target.value === "" ? NaN : Number(e.target.value))}
                />
                <small className="text-muted">
                    Number of recommenders. 5 means a book needs at least 6.
                    {preview && !changed && ` ${preview.poolSize} summaries qualify right now.`}
                </small>
            </div>

            <div className="mb-1">
                <label className="label">Today's books:</label>
                {changed ? (
                    <p className="text-muted mb-0">Save to see the books for the new number.</p>
                ) : books.length === 0 ? (
                    <p className="text-muted mb-0">
                        No summaries qualify right now — try a lower number above, then save.
                    </p>
                ) : (
                    <div className={picksStyles.autoPreview}>
                        {books.map((item) => (
                            <div key={item.summaryId} className={picksStyles.autoPreviewItem} title={item.bookName}>
                                {item.image ? (
                                    <img src={item.image} alt={item.bookName} />
                                ) : (
                                    <div className={picksStyles.autoPreviewBlank} />
                                )}
                                <span>{item.bookName}</span>
                            </div>
                        ))}
                    </div>
                )}
                <small className="text-muted">
                    Picked by the server — a random {dailyLimit} of the qualifying summaries (the
                    rail's Max in App), the same for every reader, turning over at the rail's
                    refresh rate. The app shows 12 on the rail and the rest under See All.
                </small>
            </div>
        </>
    );
}

export default EditSummarySection;
